import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useCustomerAccounts} from '@hooks/data/ims/useCustomerAccounts';
import {useDebounce} from 'use-debounce';
import {useQueryClient} from '@tanstack/react-query';

const AutocompleteOption = ({customerAccountId, name, ...rest}) => (
  <Stack
    {...rest}
    spacing={0.5}
    sx={theme => ({
      alignItems: 'start',
      ':not(:first-of-type)': {
        borderTop: 'solid 1px',
        borderTopColor: theme.palette.grey[300],
      },
      textWrap: 'wrap',
      wordBreak: 'break-word',
    })}
  >
    <Typography alignSelf="start">{name}</Typography>

    <Typography
      variant="caption"
      color="text.secondary"
      alignSelf="start"
      letterSpacing={0}
    >
      {customerAccountId}
    </Typography>
  </Stack>
);

AutocompleteOption.propTypes = {
  name: PropTypes.array,
  customerAccountId: PropTypes.string,
  props: PropTypes.object,
};

const highlightResult = (query, accountName) =>
  !query
    ? [accountName] // don't bother highlighting if there's no query
    : accountName.split(new RegExp(`(${query})`, 'i')).map((segment, index) =>
        segment.toLowerCase() === query.toLowerCase() ? (
          /* eslint-disable-next-line react/no-array-index-key */
          <strong key={`${segment}-${index}`}>{segment}</strong>
        ) : (
          segment
        )
      );

const CustomerAccountAutocomplete = ({
  limit = 8,
  onChange,
  placeholder = 'Search',
  sx,
  value,
  ...rest
}) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm.toLowerCase(), 300);

  const {isFetching, isLoading, data, error} = useCustomerAccounts(
    {
      limit,
      search: debouncedSearchTerm ? debouncedSearchTerm : '',
    },
    {
      enabled: !!debouncedSearchTerm,
      staleTime: 1000 * 60,
      keepPreviousData: false,
    }
  );

  const autocompleteOptions = data?.results ?? [];

  const handleClose = () => {
    setSearchTerm('');
    // Clear cached results when closing to prevent stale data on next open
    queryClient?.removeQueries(['ims-accounts']);
  };

  return (
    <>
      <Autocomplete
        disablePortal
        filterOptions={x => x} // needed because we're filtering on the server
        getOptionLabel={option => option.name}
        id="account-autocomplete"
        isOptionEqualToValue={(option, value) => option.id === value.id}
        ListboxProps={{sx: {paddingBottom: 0, paddingTop: 0}}}
        loading={!!searchTerm && (isFetching || isLoading)}
        name="accountAutocomplete"
        noOptionsText={
          searchTerm
            ? 'No accounts found'
            : 'Start typing to search for an account...'
        }
        onChange={(_, account) => onChange(account)}
        onClose={handleClose}
        options={autocompleteOptions}
        renderInput={params => (
          <TextField
            {...params}
            InputProps={{
              ...(params.InputProps ?? {}),
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder={placeholder}
            value={searchTerm}
          />
        )}
        renderOption={({key, ...props}, option) => (
          <AutocompleteOption
            key={option.id}
            name={highlightResult(debouncedSearchTerm, option.name)}
            customerAccountId={option.id}
            {...props}
          />
        )}
        size="small"
        slotProps={{paper: {elevation: 4}}}
        sx={sx}
        {...rest}
        value={value}
      />

      {error && (
        <Notifier
          message="Error loading customer accounts: It seems like you don't have access. Please reach out to #sh-support."
          open
          severity="error"
        />
      )}
    </>
  );
};

CustomerAccountAutocomplete.propTypes = {
  limit: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  value: PropTypes.object,
};

export default CustomerAccountAutocomplete;
